import { useEffect, useMemo, useState } from 'react';
import CountUp from 'react-countup';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';
import api from '../../services/api';

const STATUS_LIST = [
    'Análise',
    'Chefe de Área',
    'Chefe de Departamento',
    'Directora Geral Adjunta para os Serviços Executivos',
    'Director Geral',
];

function KpiCard({ label, value, color, loading }) {
    return (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-5 tw-flex tw-items-center tw-gap-4 tw-min-w-0">
            <div className="tw-min-w-0">
                <p className="tw-text-xs tw-text-gray-500 tw-uppercase tw-tracking-wide tw-font-semibold tw-truncate">
                    {label}
                </p>
                <p className="tw-text-2xl tw-font-extrabold tw-leading-tight" style={{ color }}>
                    {loading ? <span className="tw-text-gray-300 tw-animate-pulse">···</span> : <CountUp end={value} duration={1.2} separator="." />}
                </p>
            </div>
        </div>
    );
}

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-px-3 tw-py-2 tw-text-sm">
            <p className="tw-font-bold tw-text-gray-700 tw-mb-1">{label}</p>
            <p style={{ color: payload[0].fill }}>
                Total: <strong>{payload[0].value}</strong>
            </p>
        </div>
    );
}

function parseCount(responseData) {
    if (!responseData) return 0;
    if (typeof responseData?.totalItems === 'number') return responseData.totalItems;
    if (typeof responseData?.total === 'number') return responseData.total;
    if (typeof responseData?.count === 'number') return responseData.count;
    if (typeof responseData?.pagination?.totalItems === 'number') return responseData.pagination.totalItems;
    if (Array.isArray(responseData?.solicitacaoImportacaos)) return responseData.solicitacaoImportacaos.length;
    return 0;
}

export default function DashboardProdutoControladoStats() {
    const [loading, setLoading] = useState(true);
    const [statusData, setStatusData] = useState([]);

    const fetchAllRowsByStatus = async (status) => {
        const rows = [];
        let currentPage = 1;
        let keepFetching = true;

        while (keepFetching) {
            const response = await api.get(
                `/solicitacao-importacaos?solicitacaoArea=Controlados&status=${encodeURIComponent(status)}&page=${currentPage}`
            );

            const data = response?.data || {};
            const pageRows = data?.solicitacaoImportacaos || [];

            rows.push(...pageRows);

            if (data?.hasNextPage && data?.nextPage) {
                currentPage = data.nextPage;
            } else {
                keepFetching = false;
            }
        }

        return rows;
    };

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const responses = await Promise.all(
                    STATUS_LIST.map((status) =>
                        fetchAllRowsByStatus(status)
                            .then((rows) => ({ status, rows }))
                            .catch(() => ({ status, rows: [] }))
                    )
                );

                const byStatus = responses.map(({ status, rows }) => ({
                    status,
                    total: rows.length,
                }));

                const mergedRows = responses.flatMap(({ rows }) => rows || []);

                setStatusData(byStatus);

                const importadores = new Set();
                const paises = new Set();

                for (const row of mergedRows) {
                    if (row?.importador?._id) importadores.add(row.importador._id);
                    if (row?.paisOrigem) paises.add(String(row.paisOrigem).trim());
                }

                setKpiCache({
                    importador: importadores.size,
                    paisOrigem: paises.size,
                    status: byStatus.reduce((acc, item) => acc + (item.total || 0), 0),
                    totalRows: mergedRows.length,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, []);

    const [kpiCache, setKpiCache] = useState({
        importador: 0,
        paisOrigem: 0,
        status: 0,
        totalRows: 0,
    });

    const kpis = useMemo(() => kpiCache, [kpiCache]);

    return (
        <div className="tw-space-y-5">
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                <KpiCard label="Importadores" value={kpis.importador} color="#85287e" loading={loading} />
                <KpiCard label="País de Origem" value={kpis.paisOrigem} color="#2563eb" loading={loading} />
                <KpiCard label="Status" value={kpis.status} color="#0891b2" loading={loading} />
            </div>

            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-5">
                <p className="tw-text-sm tw-font-bold tw-text-gray-700 tw-mb-4 tw-uppercase tw-tracking-wide">
                    Distribuição por Status
                </p>

                {loading ? (
                    <div className="tw-h-72 tw-flex tw-items-center tw-justify-center tw-text-gray-300 tw-text-sm">A carregar...</div>
                ) : (
                    <ResponsiveContainer width="100%" height={340}>
                        <BarChart data={statusData} margin={{ top: 0, right: 10, left: 0, bottom: 40 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis
                                dataKey="status"
                                angle={-15}
                                textAnchor="end"
                                interval={0}
                                height={70}
                                tick={{ fontSize: 11, fill: '#6b7280' }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                tick={{ fontSize: 11, fill: '#6b7280' }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="total" fill="#85287e" radius={[6, 6, 0, 0]} maxBarSize={52} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>

            {!loading && kpis.totalRows === 0 && (
                <div className="tw-rounded-xl tw-border tw-border-dashed tw-border-gray-300 tw-bg-white tw-p-4 tw-text-sm tw-text-gray-600">
                    Sem dados retornados para Produto Controlado com os status configurados.
                </div>
            )}
        </div>
    );
}
